/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useRawScriptInclude } from '@/hooks/use-raw-script-include';

export interface OmnitureFooterIncludeProps {
    omnitureEnabled?: boolean;
    omnitureFooterInclude?: string | null;
    /** CSP nonce for inline scripts; null/undefined when security headers middleware is disabled. */
    nonce?: string | null;
}

/**
 * Injects the `omnitureFooterInclude` site preference at the end of
 * `document.body` — the Storefront Next equivalent of the raw `<isprint
 * value="${...}" encoding="off" />` block in the legacy
 * `footer.isml`/`footer_checkout.isml` templates. Rendered once, globally,
 * in `root.tsx` (rather than duplicated into each footer variant) so it
 * fires on every page regardless of layout, matching the legacy behavior.
 *
 * Injection is imperative (`useRawScriptInclude`) rather than a JSX
 * `<script>` — see that hook's doc comment for why: a JSX `<script async
 * src>` is treated by React as a hoistable resource and gets moved into
 * `<head>` regardless of where it's rendered, which would silently defeat
 * the point of a *footer* include.
 *
 * Renders nothing (the include has no visible/JSX output) when Omniture
 * isn't enabled or no footer include is configured.
 */
export function OmnitureFooterInclude({ omnitureEnabled, omnitureFooterInclude, nonce }: OmnitureFooterIncludeProps) {
    useRawScriptInclude(omnitureEnabled ? omnitureFooterInclude : null, nonce, () => document.body);
    return null;
}
